import { logger } from "@/lib/logger";
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteContext } from "@/contexts/SiteContext";
import { Skeleton } from "@/components/ui/skeleton";
import { mapProfileRoleToSOPRole } from "@/lib/entities/kind";
import { useToast } from "@/hooks/use-toast";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { ModuleNotActivated } from "@/components/auth/ModuleNotActivated";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
  requireAdmin?: boolean;
  /**
   * Escrow module gate. Uses public.has_module_access RPC — fails closed.
   * Standard-tier modules (e.g. 'phc-core') are always allowed for authenticated
   * users, so most routes don't need this prop.
   */
  requireModule?: string;
}


export function ProtectedRoute({ children, requiredRole, requireAdmin, requireModule }: ProtectedRouteProps) {
  const moduleAccess = useModuleAccess(requireModule);

  const { user, userProfile, loading, isAdmin, primaryRole, adminLoading, isAuthReady } = useAuth();
  const { site } = useSiteContext();
  const location = useLocation();
  const { toast } = useToast();
  const [adminWaitTimeout, setAdminWaitTimeout] = useState(false);
  const [toastShown, setToastShown] = useState(false);

  // Timeout mechanism for admin checks (prevent infinite wait) - MUST be before any early returns
  useEffect(() => {
    if (requireAdmin && !isAdmin && !primaryRole && !userProfile?.role) {
      const timer = setTimeout(() => {
        setAdminWaitTimeout(true);
      }, 4000); // 4-second timeout to prevent race condition with RPC calls
      
      return () => clearTimeout(timer);
    }
  }, [requireAdmin, isAdmin, primaryRole, userProfile?.role]);

  // Show toast only once when timeout occurs and user lacks admin access
  useEffect(() => {
    if (adminWaitTimeout && requireAdmin && !toastShown) {
      const roleStr = (primaryRole ?? userProfile?.role ?? '').toLowerCase();
      const hasAdmin = isAdmin || roleStr === 'admin' || roleStr === 'super_admin';
      
      if (!hasAdmin) {
        toast({
          title: "Access Verification",
          description: "Unable to confirm admin access. Redirected to your dashboard.",
          variant: "default",
        });
        setToastShown(true);
      }
    }
  }, [adminWaitTimeout, requireAdmin, isAdmin, primaryRole, userProfile?.role, toastShown, toast]);

  logger.devLog("ProtectedRoute state", {
    loading,
    adminLoading,
    isAuthReady,
    hasUser: !!user,
    isAdmin,
    primaryRole,
    profileRole: userProfile?.role,
    requireAdmin,
    requiredRole
  });

  // Wait for auth to be fully ready (both session and profile loaded)
  if (loading || adminLoading || !isAuthReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="space-y-4 w-full max-w-md">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to auth page
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check for admin role if required - use immediate profile role as fallback with better NULL handling
  if (requireAdmin) {
    // Block admin routes on white-label subdomains — admin UI should never appear on branded portals
    if (site && (site.type === 'cro' || site.type === 'sponsor' || site.type === 'trial_site' || site.type === 'government' || site.type === 'phc')) {
      logger.devLog("ProtectedRoute redirect: admin route blocked on white-label subdomain", {
        siteType: site.type,
        siteSlug: site.slug,
      });
      return <Navigate to="/" replace />;
    }
    // Prioritize server-verified isAdmin, then check roles from multiple sources
    const roleStr = (primaryRole ?? userProfile?.role ?? '').toLowerCase();
    const roleKnown = !!roleStr;
    
    // If we have role from profile, use it immediately (no wait needed)
    const hasAdminFromProfile = roleStr === 'admin' || roleStr === 'super_admin';
    
    // Wait for admin evidence before making decision, but with 4s timeout
    if (!isAdmin && !roleKnown && !hasAdminFromProfile && !adminWaitTimeout) {
      logger.devLog("ProtectedRoute admin check: waiting for role/admin evidence");
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="space-y-4 w-full max-w-md">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      );
    }
    
    const hasAdmin = isAdmin || roleStr === 'admin' || roleStr === 'super_admin';
    
    logger.devLog("ProtectedRoute admin check", { 
      requireAdmin, 
      effectiveRole: roleStr, 
      isAdmin, 
      hasAdmin,
      primaryRole,
      profileRole: userProfile?.role,
      timedOut: adminWaitTimeout
    });
    
    if (!hasAdmin) {
      logger.devLog("ProtectedRoute redirect: missing admin", { 
        to: "/", 
        roleStr, 
        isAdmin,
        timedOut: adminWaitTimeout
      });
      return <Navigate to="/" replace />;
    }
  }

  // Check for specific required role with mapping support
  const effectiveRole = primaryRole ?? userProfile?.role;
  
  // ✅ ADMIN/SUPER_ADMIN BYPASS - They have access to any route regardless of requiredRole
  const isAdminUser = effectiveRole === 'admin' || effectiveRole === 'super_admin' || isAdmin;
  if (isAdminUser && requiredRole && !requireAdmin) {
    logger.devLog("ProtectedRoute: Admin bypass for requiredRole", { 
      requiredRole, 
      effectiveRole,
      isAdmin 
    });
    return <>{children}</>;
  }
  
  // ✅ WHITE-LABEL SITE CONTEXT BYPASS - Allow access based on subdomain type
  if (site && requiredRole) {
    const siteAllowsAccess = 
      (site.type === 'cro' && requiredRole === 'cro') ||
      (site.type === 'sponsor' && requiredRole === 'sponsor') ||
      (site.type === 'trial_site' && ['trial_site', 'site_director', 'medical_professional'].includes(requiredRole));
    
    if (siteAllowsAccess) {
      logger.devLog("ProtectedRoute: Site context bypass", { 
        siteType: site.type, 
        siteSlug: site.slug,
        requiredRole 
      });
      return <>{children}</>;
    }
  }
  
  // Role mismatch check for non-admin users on main domain
  if (requiredRole && effectiveRole) {
    // Map both the effective role and required role for consistent comparison
    const mappedRole = mapProfileRoleToSOPRole(effectiveRole);
    const mappedRequiredRole = mapProfileRoleToSOPRole(requiredRole);
    
    if (mappedRole !== mappedRequiredRole) {
      logger.devLog("ProtectedRoute redirect: role mismatch", { 
        requiredRole, 
        effectiveRole, 
        mappedRole,
        mappedRequiredRole,
        to: "/" 
      });
      return <Navigate to="/" replace />;
    }
  }

  // Escrow module gate — fails closed. Admins bypass via has_module_access itself.
  if (requireModule) {
    if (moduleAccess.loading) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="space-y-4 w-full max-w-md">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      );
    }
    if (!moduleAccess.hasAccess) {
      return <ModuleNotActivated moduleCode={requireModule} />;
    }
  }

  return <>{children}</>;
}

